import React, { useEffect, useRef, useState } from 'react';
import { searchRemoteEpg } from '../../components/Remote/deviceRemoteApi';
import { displayWindow } from '../../components/Fixtures/sportsTime';

export default function RemoteEpgSearch({ targetId, enabled, timeZone, onNavigate, onClear, resetKey, variant = 'epg' }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [retry, setRetry] = useState(0);
  const [state, setState] = useState({ results: [], loading: false, error: '' });
  const input = useRef(null);
  const search = query.trim();
  const resultsId = `${variant}-programme-search-results`;
  useEffect(() => {
    if (resetKey === undefined) return;
    setQuery(''); setOpen(false); input.current?.blur();
  }, [resetKey]);
  useEffect(() => {
    if (!enabled) { setOpen(false); input.current?.blur(); }
  }, [enabled]);
  useEffect(() => {
    setState({ results: [], loading: enabled && open && search.length >= 2, error: '' });
    if (!enabled || !open || search.length < 2) return undefined;
    let active = true; const abort = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const result = await searchRemoteEpg(targetId, search, abort.signal);
        if (active) setState({ ...result, loading: false, error: '' });
      } catch {
        if (active) setState({ results: [], loading: false, error: 'Search unavailable.' });
      }
    }, 250);
    return () => { active = false; abort.abort(); clearTimeout(timer); };
  }, [targetId, enabled, open, search, retry]);
  const close = () => { setOpen(false); input.current?.blur(); };
  const clear = () => { setQuery(''); onClear?.(); close(); };
  const message = search.length < 2 ? 'Type 2+ letters · published listings, next 14 days'
    : state.loading ? 'Searching…' : state.error || (!state.results.length ? 'No matching listings.'
      : `${state.truncated ? 'First ' : ''}${state.results.length} listings${state.truncated ? ' · narrow your search' : ''}`);
  return <div className={`remote-epg__search${variant === 'launcher' ? ' remote-epg__search--launcher' : ''}`} role="search" aria-label="Programme search" data-open={open}>
    <form className="remote-epg__search-form" onSubmit={(event) => { event.preventDefault(); input.current?.blur(); }}>
      <input ref={input} type="search" inputMode="search" enterKeyHint="search" autoComplete="off" autoCapitalize="none" spellCheck={false}
        aria-label="Search programmes" aria-expanded={open} aria-controls={resultsId}
        placeholder="Search programmes…" value={query} maxLength={100} disabled={!enabled}
        onFocus={() => setOpen(true)} onChange={(event) => { setQuery(event.target.value); setOpen(true); if (!event.target.value.trim()) onClear?.(); }}
        onKeyDown={(event) => { if (event.key === 'Escape') clear(); }} />
      {open && <><button type="submit" aria-label="Hide keyboard and keep search results">Done</button>
        <button type="button" aria-label="Close programme search" onClick={clear}>×</button></>}
    </form>
    {open && <div className="remote-epg__search-panel" id={resultsId}>
      <div className="remote-epg__search-status" role="status">{message}{state.error && <button type="button" onClick={() => setRetry((value) => value + 1)}>Retry</button>}</div>
      {state.results.length > 0 && <ul className="remote-epg__search-results" aria-label="Programme search results" tabIndex={0}>
        {state.results.map((result) => <li key={`${result.channel.id}:${result.programme.id}`}>
          <button type="button" onClick={() => { close(); onNavigate(result); }}>
            <strong>{result.programme.title}</strong>
            <span>{[result.channel.name, result.channel.appName, result.channel.deviceName].filter(Boolean).join(' · ')}</span>
            <time dateTime={result.programme.startsAt}>{displayWindow(result.programme.startsAt, result.programme.endsAt, state.timeZone || timeZone)}</time>
          </button>
        </li>)}
      </ul>}
    </div>}
  </div>;
}
